import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useVideoPreviewSettingsContext } from '../VideoPreviewSettingsContext'
import { useUpdateVideoDetailsMutation } from '../../../blocs/usePreviewVideoPage'

import './VideoPreviewTitle.scss'

interface VideoPreviewTitleProps {
  title?: string
}

const VideoPreviewTitle = ({ title }: VideoPreviewTitleProps) => {
  const { headlineFont, headlineFontSize } = useVideoPreviewSettingsContext()
  const style = {
    fontFamily: headlineFont,
    fontSize: `${headlineFontSize}px`,
  }

  return (
    <h1 className="view-title" style={style}>
      {title || 'Untitled'}
    </h1>
  )
}

interface EditableVideoPreviewTitleProps {
  videoId: number
  title?: string
}
const EditableVideoPreviewTitle = ({
  videoId,
  title,
}: EditableVideoPreviewTitleProps) => {
  const { mutateAsync: updateVideoDetails } = useUpdateVideoDetailsMutation()
  const ref = useRef<HTMLHeadingElement>(null)
  const [isEmpty, setIsEmpty] = useState(!title?.trim())

  useEffect(() => {
    if (ref.current) {
      ref.current.textContent = title ?? ''
    }
    setIsEmpty(!title?.trim())
  }, [title])

  const handleTitleChange = useCallback(
    async (newTitle: string) => {
      await updateVideoDetails({
        videoId,
        title: newTitle.trim(),
      })
    },
    [updateVideoDetails, videoId]
  )

  const { headlineFont, headlineFontSize } = useVideoPreviewSettingsContext()
  const style = {
    fontFamily: headlineFont,
    fontSize: `${headlineFontSize}px`,
  }
  return (
    // eslint-disable-next-line jsx-a11y/heading-has-content
    <h1
      ref={ref}
      className="video-preview-title-editable"
      style={style}
      aria-label="Video Title"
      /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role */
      role="textbox"
      contentEditable
      suppressContentEditableWarning
      data-placeholder="Untitled"
      data-is-empty={isEmpty}
      /* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */
      onInput={(e) => setIsEmpty(!(e.currentTarget.textContent ?? '').trim())}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          e.currentTarget.blur()
        }
      }}
      onBlur={(e) => void handleTitleChange(e.currentTarget.textContent || '')}
    />
  )
}

interface VideoPreviewTitleDuplexerProps {
  videoId: number
  title?: string
  isEditable?: boolean
}
const VideoPreviewTitleDuplexer = ({
  videoId,
  title,
  isEditable = false,
}: VideoPreviewTitleDuplexerProps) => {
  return isEditable ? (
    <EditableVideoPreviewTitle videoId={videoId} title={title} />
  ) : (
    <VideoPreviewTitle title={title} />
  )
}

export { VideoPreviewTitleDuplexer as VideoPreviewTitle }
